// Momentum Chart: a detailed, horizontally-scrollable per-rally timeline
// across every set of a match. Ported from DVMate's iOS BeachMomentumChartView:
//   /Users/chaule/Projects/DVMate/TeamSport/Reports/Momentum Chart/BeachMomentumChartView.m
// (+ Drill+sup.m's ralliesForSet/createRally for the rally-outcome/sideout
// semantics: outcome==0 = home scored, sideout = the RECEIVING team won the
// rally). That view draws against a single-team-tracked data model
// (MatchRally, with synthetic "opposition" event types); VBLive already
// parses BOTH teams' full event detail (see buildPointByPoint in
// ../utils/PointByPoint.js), so this port reuses that shared helper.
//
// Each rally is one column, split into 9 stacked rows (top to bottom):
//   1. Score at the serve event (pre-rally score)
//   2. Team A's rotation when the rally started
//   3. Team A's serve (circle, colour-by-grade; thick outline = serve ace)
//   4. Team A's serve-receive (square) + sideout bar underneath
//   5. Team A's key event (the event that ended the rally, if by a team A
//      player) — player name + skill label, both rotated 90°, side by side
//   --- centre momentum line separates the two teams' data ---
//   6. Team B's key event (mirrors row 5)
//   7. Team B's serve-receive (square) + sideout bar ABOVE it
//   8. Team B's serve (circle)
//   9. Team B's rotation when the rally started
// Only one of (serve circle) / (receive square) is populated per team per
// rally (whichever team served vs received that point); the key-event row is
// only populated on whichever team's player actually ended the rally.
//
// Sideout bar colour approximates DVMate's sideout/sideoutFirstBall/
// sideoutOppError/sideoutSpikeKill (green = sideout, red = server held
// serve); a full first-ball-vs-transition distinction would need deeper
// rally-phase tracking not reconstructed here, so only the two most common
// sub-cases are called out with a small accent bar (yellow = sideout ending
// on the receiving team's own kill, pink = sideout ending on the server's
// own error).
// NOT yet ported: timeout markers (DVWFile.js does not currently parse
// timeout events from DVW at all, so there's no data for these yet).
import { useMemo } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import { getRotationLabels } from "../utils/Constants";
import { buildPointByPoint, colourByGrade, skillLabel } from "../utils/PointByPoint";

const kHomeColour = "#2563eb";
const kAwayColour = "#f97316";
const kBarWidth = 34;
const kScoreH = 18;
const kRowH = 16;
const kServeH = 22;
const kCircleR = 11;
const kSquareSize = kCircleR * 2;
const kReceiveH = kSquareSize + 4;
const kSideoutH = 8;
const kKeyEventH = 64;
const kKeyFontSize = kKeyEventH / 6;
const kMaxKeyTextLen = 8;

// Key-event text truncates to fit the rotated column (DVMate shows the full
// name/skill on a much wider iOS view; here it must fit kKeyEventH's width).
function truncateLabel(str) {
  if (!str) return "";
  return str.length > kMaxKeyTextLen ? str.slice(0, kMaxKeyTextLen - 1) + "…" : str;
}

// Green = positive outcome (kill/ace, EventGrade 3), red = error (EventGrade 0).
function keyEventColour(grade) {
  if (grade === 3) return "#16a34a";
  if (grade === 0) return "#dc2626";
  return "currentColor";
}

const yRowATop = kScoreH;
const yServeATop = yRowATop + kRowH;
const yReceiveATop = yServeATop + kServeH;
const ySideoutATop = yReceiveATop + kReceiveH;
const yKeyATop = ySideoutATop + kSideoutH;
const yCentre = yKeyATop + kKeyEventH;
const ySideoutBTop = yCentre + kKeyEventH;
const yReceiveBTop = ySideoutBTop + kSideoutH;
const yServeBTop = yReceiveBTop + kReceiveH;
const yRowBTop = yServeBTop + kServeH;
const kTotalHeight = yRowBTop + kRowH;

function buildMatchPoints(match) {
  const sets = match.sets ?? match.drills ?? [];
  let maxGap = 0;
  const setPoints = sets.map((set, idx) => {
    const points = buildPointByPoint(set);
    for (const p of points) {
      maxGap = Math.max(maxGap, Math.abs(p.pointsA - p.pointsB));
    }
    return { setNumber: idx + 1, set, points };
  });
  return { setPoints, maxGap: maxGap + 2 };
}

function MomentumChart({ matches, selectedRally, onRallyClick, highlightA, highlightB }) {
  const { t } = useLanguage();
  const match = matches?.[0];
  const rotationLabels = getRotationLabels();

  // Rally identifiers ({setNumber, index}) whose rotation cell should be
  // highlighted — set by clicking a stat in MomentumStatsPanel.jsx.
  const highlightASet = useMemo(
    () => new Set((highlightA ?? []).map((r) => `${r.setNumber}-${r.index}`)),
    [highlightA]
  );
  const highlightBSet = useMemo(
    () => new Set((highlightB ?? []).map((r) => `${r.setNumber}-${r.index}`)),
    [highlightB]
  );

  const { setPoints, maxGap } = useMemo(
    () => (match ? buildMatchPoints(match) : { setPoints: [], maxGap: 2 }),
    [match]
  );

  if (!match) return null;
  const totalPoints = setPoints.reduce((n, s) => n + s.points.length, 0);
  if (totalPoints === 0) {
    return (
      <div className="alert alert-info m-4">
        <span>{t("No point-by-point data available for this match.")}</span>
      </div>
    );
  }

  const stepUnit = kKeyEventH / maxGap;
  const width = totalPoints * kBarWidth;

  // x advances continuously across the whole match (sets laid out side by
  // side), but the momentum line resets to the centre at the start of every
  // set — each set's momentum is independent.
  let x = 0;
  const pathParts = [];
  const columns = [];
  const setDividers = [];
  for (const { setNumber, set, points } of setPoints) {
    let y = yCentre;
    setDividers.push({ x, setNumber, scoreA: set.HomeScore ?? 0, scoreB: set.AwayScore ?? 0 });
    pathParts.push(`M ${x} ${y}`);
    for (const p of points) {
      const step = p.scoringTeam === "A" ? -stepUnit : p.scoringTeam === "B" ? stepUnit : 0;
      pathParts.push(`L ${x + kBarWidth} ${y}`);
      const nextY = y + step;
      pathParts.push(`L ${x + kBarWidth} ${nextY}`);
      columns.push({ ...p, setNumber, x });
      y = nextY;
      x += kBarWidth;
    }
  }

  // Sideout bar colour/accent for one side's row (only drawn when that side
  // was the RECEIVING team this rally).
  const sideoutFill = (p, side) => {
    if (p.receiver !== side) return null;
    return p.sideout ? "#16a34a" : "#dc2626";
  };
  const sideoutAccent = (p, side) => {
    if (p.receiver !== side || !p.sideoutSubtype) return null;
    return p.sideoutSubtype === "kill" ? "#facc15" : "#ec4899";
  };

  return (
    <div className="flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-center gap-4 text-xs opacity-70">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "#2ecc71" }} />
          {t("Ace / Kill")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "#f39c12" }} />
          {t("In play")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "#e74c3c" }} />
          {t("Error")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2" style={{ backgroundColor: "#16a34a" }} />
          {t("Sideout")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2" style={{ backgroundColor: "#dc2626" }} />
          {t("Break point")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2" style={{ backgroundColor: "#facc15" }} />
          {t("Sideout on a kill")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2" style={{ backgroundColor: "#ec4899" }} />
          {t("Sideout on an error")}
        </span>
      </div>

      <div className="overflow-x-auto border border-base-300 rounded-md">
        <svg width={width} height={kTotalHeight} className="bg-base-100">
          <line x1={0} y1={yCentre} x2={width} y2={yCentre} stroke="currentColor" strokeOpacity={0.25} />

          {setDividers.map((d, i) => {
            const nextX = setDividers[i + 1]?.x ?? width;
            return (
              <g key={d.setNumber}>
                <rect x={d.x} y={0} width={nextX - d.x} height={kScoreH} fill="currentColor" fillOpacity={0.07} />
                <text x={d.x + 4} y={kScoreH / 2 + 4} fontSize={9} fontWeight="bold">
                  {t("Set")} {d.setNumber} — {d.scoreA}-{d.scoreB}
                </text>
                {i > 0 && (
                  <line x1={d.x} y1={0} x2={d.x} y2={kTotalHeight} stroke="currentColor" strokeOpacity={0.3} />
                )}
              </g>
            );
          })}

          {/* Centre momentum line */}
          <path d={pathParts.join(" ")} fill="none" stroke="#2563eb" strokeWidth={2} strokeOpacity={0.7} />

          {columns.map((p) => {
            const cx = p.x + kBarWidth / 2;
            const serveAce = p.scoringTeam === p.server;
            const soFillA = sideoutFill(p, "A");
            const soAccentA = sideoutAccent(p, "A");
            const soFillB = sideoutFill(p, "B");
            const soAccentB = sideoutAccent(p, "B");

            return (
              <g
                key={`${p.setNumber}-${p.index}`}
                onClick={() => onRallyClick?.(p)}
                style={onRallyClick ? { cursor: "pointer" } : undefined}
              >
                {/* Row 1: score at the serve — highlighted when this rally is selected */}
                <rect
                  x={p.x}
                  y={0}
                  width={kBarWidth}
                  height={kScoreH}
                  fill={
                    selectedRally?.setNumber === p.setNumber && selectedRally?.index === p.index
                      ? "#fbbf24"
                      : "transparent"
                  }
                />
                <text x={cx} y={kScoreH / 2 + 4} fontSize={10} textAnchor="middle" opacity={0.8}>
                  {p.preA}-{p.preB}
                </text>

                {/* Row 2: Team A rotation — highlighted when this rally contributed
                    to the stat clicked in MomentumStatsPanel.jsx */}
                <rect
                  x={p.x}
                  y={yRowATop}
                  width={kBarWidth}
                  height={kRowH}
                  fill={highlightASet.has(`${p.setNumber}-${p.index}`) ? "#38bdf8" : "transparent"}
                  fillOpacity={0.5}
                />
                <text x={cx} y={yRowATop + kRowH / 2 + 4} fontSize={9} textAnchor="middle" opacity={0.65}>
                  {p.rowA != null ? rotationLabels[p.rowA - 1] ?? `R${p.rowA}` : ""}
                </text>

                {/* Row 3: Team A serve */}
                {p.server === "A" && (
                  <>
                    <circle
                      cx={cx}
                      cy={yServeATop + kServeH / 2}
                      r={kCircleR}
                      fill={colourByGrade(p.serveEvent.EventGrade)}
                      fillOpacity={0.55}
                      stroke={kHomeColour}
                      strokeWidth={serveAce ? 3 : 1}
                    />
                    <text x={cx} y={yServeATop + kServeH / 2 + 3} fontSize={kCircleR} textAnchor="middle">
                      {p.serveEvent.Player?.shirtNumber ?? ""}
                    </text>
                  </>
                )}

                {/* Row 4: Team A receive + sideout bar */}
                {p.receiver === "A" && p.receiveEvent && (
                  <>
                    <rect
                      x={cx - kSquareSize / 2}
                      y={yReceiveATop + (kReceiveH - kSquareSize) / 2}
                      width={kSquareSize}
                      height={kSquareSize}
                      fill={colourByGrade(p.receiveEvent.EventGrade)}
                      fillOpacity={0.5}
                      stroke={kHomeColour}
                      strokeWidth={1}
                    />
                    <text x={cx} y={yReceiveATop + kReceiveH / 2 + 3} fontSize={kCircleR} textAnchor="middle">
                      {p.receiveEvent.Player?.shirtNumber ?? ""}
                    </text>
                  </>
                )}
                {soFillA && (
                  <rect x={cx - kCircleR} y={ySideoutATop} width={kCircleR * 2} height={kSideoutH} fill={soFillA} fillOpacity={0.6} />
                )}
                {soAccentA && (
                  <rect x={cx - 2} y={ySideoutATop} width={4} height={kSideoutH} fill={soAccentA} />
                )}

                {/* Row 5: Team A key event */}
                {p.keyEventTeam === "A" && (
                  <>
                    <text
                      x={p.x + kBarWidth * 0.3}
                      y={yKeyATop + kKeyEventH * 0.55}
                      fontSize={kKeyFontSize}
                      textAnchor="middle"
                      fill={keyEventColour(p.keyEvent.EventGrade)}
                      transform={`rotate(-90 ${p.x + kBarWidth * 0.3} ${yKeyATop + kKeyEventH * 0.55})`}
                    >
                      {truncateLabel((p.keyEvent.Player?.NickName ?? "").toUpperCase())}
                    </text>
                    <text
                      x={p.x + kBarWidth * 0.7}
                      y={yKeyATop + kKeyEventH * 0.55}
                      fontSize={kKeyFontSize - 2}
                      textAnchor="middle"
                      fill={keyEventColour(p.keyEvent.EventGrade)}
                      opacity={0.8}
                      transform={`rotate(-90 ${p.x + kBarWidth * 0.7} ${yKeyATop + kKeyEventH * 0.55})`}
                    >
                      {truncateLabel(skillLabel(p.keyEvent)).toUpperCase()}
                    </text>
                  </>
                )}

                {/* Row 6: Team B key event */}
                {p.keyEventTeam === "B" && (
                  <>
                    <text
                      x={p.x + kBarWidth * 0.3}
                      y={yCentre + kKeyEventH * 0.45}
                      fontSize={kKeyFontSize}
                      textAnchor="middle"
                      fill={keyEventColour(p.keyEvent.EventGrade)}
                      transform={`rotate(-90 ${p.x + kBarWidth * 0.3} ${yCentre + kKeyEventH * 0.45})`}
                    >
                      {truncateLabel((p.keyEvent.Player?.NickName ?? "").toUpperCase())}
                    </text>
                    <text
                      x={p.x + kBarWidth * 0.7}
                      y={yCentre + kKeyEventH * 0.45}
                      fontSize={kKeyFontSize - 2}
                      textAnchor="middle"
                      fill={keyEventColour(p.keyEvent.EventGrade)}
                      opacity={0.8}
                      transform={`rotate(-90 ${p.x + kBarWidth * 0.7} ${yCentre + kKeyEventH * 0.45})`}
                    >
                      {truncateLabel(skillLabel(p.keyEvent)).toUpperCase()}
                    </text>
                  </>
                )}

                {/* Row 7: Team B receive (bar above square) + sideout bar */}
                {soFillB && (
                  <rect x={cx - kCircleR} y={ySideoutBTop} width={kCircleR * 2} height={kSideoutH} fill={soFillB} fillOpacity={0.6} />
                )}
                {soAccentB && (
                  <rect x={cx - 2} y={ySideoutBTop} width={4} height={kSideoutH} fill={soAccentB} />
                )}
                {p.receiver === "B" && p.receiveEvent && (
                  <>
                    <rect
                      x={cx - kSquareSize / 2}
                      y={yReceiveBTop + (kReceiveH - kSquareSize) / 2}
                      width={kSquareSize}
                      height={kSquareSize}
                      fill={colourByGrade(p.receiveEvent.EventGrade)}
                      fillOpacity={0.5}
                      stroke={kAwayColour}
                      strokeWidth={1}
                    />
                    <text x={cx} y={yReceiveBTop + kReceiveH / 2 + 3} fontSize={kCircleR} textAnchor="middle">
                      {p.receiveEvent.Player?.shirtNumber ?? ""}
                    </text>
                  </>
                )}

                {/* Row 8: Team B serve */}
                {p.server === "B" && (
                  <>
                    <circle
                      cx={cx}
                      cy={yServeBTop + kServeH / 2}
                      r={kCircleR}
                      fill={colourByGrade(p.serveEvent.EventGrade)}
                      fillOpacity={0.55}
                      stroke={kAwayColour}
                      strokeWidth={serveAce ? 3 : 1}
                    />
                    <text x={cx} y={yServeBTop + kServeH / 2 + 3} fontSize={kCircleR} textAnchor="middle">
                      {p.serveEvent.Player?.shirtNumber ?? ""}
                    </text>
                  </>
                )}

                {/* Row 9: Team B rotation — highlighted when this rally contributed
                    to the stat clicked in MomentumStatsPanel.jsx */}
                <rect
                  x={p.x}
                  y={yRowBTop}
                  width={kBarWidth}
                  height={kRowH}
                  fill={highlightBSet.has(`${p.setNumber}-${p.index}`) ? "#38bdf8" : "transparent"}
                  fillOpacity={0.5}
                />
                <text x={cx} y={yRowBTop + kRowH / 2 + 4} fontSize={9} textAnchor="middle" opacity={0.65}>
                  {p.rowB != null ? rotationLabels[p.rowB - 1] ?? `R${p.rowB}` : ""}
                </text>

                <line x1={p.x + kBarWidth} y1={kScoreH} x2={p.x + kBarWidth} y2={kTotalHeight} stroke="currentColor" strokeOpacity={0.08} />
              </g>
            );
          })}
        </svg>
      </div>
      <p className="text-xs opacity-50">
        {t("Top half")}: {match.teamA?.Name}. {t("Bottom half")}: {match.teamB?.Name}.{" "}
        {t("The centre line steps toward the team that wins each point.")}
      </p>
    </div>
  );
}

export default MomentumChart;

